/* ============================================================
   Joy Loyalty Widget — DUE Studio: minimal popup blocker + redirect
                        + floating-button label enforcement

   Destinations:
   - FAB (bottom-left)       →  Personal area (theme page)
   - Any other popup trigger →  Shopify customer accounts DUE LIST page
     ("history of benefits", "redeem now", hero-banner buttons, #joy-open, etc.)

   LABEL OVERRIDE — why this exists, and what it is not:
   Joy's launcher label is app-controlled, not theme-controlled. Joy's own API
   (GET https://joy.avada.io/app/api/v1/popup/info?d=<shop>) reports, as of
   2026-08-20:
       settings.floatingButtonText = "JOIN DUE LIST"   <- set correctly in Joy
       settings.floatBtnLabel      = "Rewards"         <- legacy field, default
       launcher.design             = "label_only"
   i.e. the value was saved in Joy but the launcher still renders the legacy
   field. The correct fix is in Joy (clear/replace floatBtnLabel, or have Joy
   support drop it); this is a deliberate client-side cover-up, applied on the
   merchant's instruction, until Joy is corrected.

   Consequences, on purpose stated: the original label paints for a frame or two
   before this rewrites it, and nothing here runs if Joy stops shipping a
   launcher or if this asset is not on the published theme. Rewrites are
   restricted to launcher elements whose text is one of WRONG_LABELS, so the
   widget's own "Rewards"/"Reward List" tabs are never touched.
   ============================================================ */
(function () {
  'use strict';

  try { console.log('[due-joy] v7 direct-fab + label loaded', new Date().toISOString()); } catch (_) {}

  // The label the merchant wants on the floating button.
  var FAB_LABEL = 'JOIN DUE LIST';
  // Only these get replaced. Anything else is left alone, so a future correct
  // label from Joy is never clobbered.
  var WRONG_LABELS = ['reward', 'rewards'];

  var PERSONAL_AREA_URL = '/pages/%D7%9E%D7%99%D7%93%D7%A2-%D7%95%D7%90%D7%96%D7%95%D7%A8-%D7%90%D7%99%D7%A9%D7%99';
  // Set in <head> by snippets/due-login-redirect.liquid, so the account-page URL
  // lives in one place. Literal kept as a fallback if that snippet is removed.
  var DUE_LIST_URL = window.DUE_LIST_URL || 'https://shopify.com/62481465423/account/pages/019d1ed2-a26a-7801-a8ee-1cd008d0abd8';

  // Track when the FAB was recently interacted with, so that a subsequent
  // widget-open (caused by Joy handling the same click before our handler
  // could preventDefault) still routes to the personal area, not DUE LIST.
  var fabInteractionAt = 0;
  function markFab() { fabInteractionAt = Date.now(); }
  function wasRecentFab() { return (Date.now() - fabInteractionAt) < 1500; }

  var redirecting = false;
  function go(url, label, e) {
    if (redirecting) return;
    redirecting = true;
    try { console.log('[due-joy] redirect →', label, url); } catch (_) {}
    if (e) {
      try { e.preventDefault(); } catch (_) {}
      try { e.stopImmediatePropagation(); } catch (_) {}
      try { e.stopPropagation(); } catch (_) {}
    }
    window.location.assign(url);
  }

  // ---- 1) CLICK: FAB → personal area, #joy-open link → DUE LIST url ----
  function onClick(e) {
    var path = (e.composedPath && e.composedPath()) || [];
    for (var i = 0; i < path.length; i++) {
      var el = path[i];
      if (!el || el.nodeType !== 1) continue;
      var tag = (el.tagName || '').toLowerCase();

      if (tag === 'joy-fab-button') {
        markFab();
        go(PERSONAL_AREA_URL, 'FAB click', e);
        return;
      }
      if (el.classList && (el.classList.contains('joy-fab-pure') || el.classList.contains('joy-fab-label') || el.classList.contains('fab'))) {
        markFab();
        go(PERSONAL_AREA_URL, 'FAB class click', e);
        return;
      }
      // Accepts the wanted label AND the labels Joy currently ships, so the
      // routing does not depend on which text happens to be rendered.
      if (el.getAttribute && isFabLabel(el.getAttribute('aria-label'))) {
        markFab();
        go(PERSONAL_AREA_URL, 'FAB aria click', e);
        return;
      }
      if (tag === 'a' && el.getAttribute) {
        var href = el.getAttribute('href') || '';
        if (href.indexOf('#joy-open') !== -1) {
          go(DUE_LIST_URL, 'anchor #joy-open', e);
          return;
        }
      }
    }
  }
  ['pointerdown', 'click'].forEach(function (ev) {
    document.addEventListener(ev, onClick, true);
  });

  // ---- 2) URL hash on load ----
  if (window.location.hash === '#joy-open') {
    go(DUE_LIST_URL, 'hash on load');
    return;
  }

  // ---- 3) Observe widget: whenever `open` attr is set OR .joy-widget-container
  //          gains .joy-open, close it and redirect to DUE LIST url.
  //          (FAB click already handled above — this catches everything else.)
  function watchWidget(widget) {
    if (widget.__dueWatched) return;
    widget.__dueWatched = true;

    // Outer `open` attribute
    new MutationObserver(function () {
      if (widget.hasAttribute('open') && !redirecting) {
        widget.removeAttribute('open');
        go(wasRecentFab() ? PERSONAL_AREA_URL : DUE_LIST_URL, wasRecentFab() ? 'widget[open] set — recent FAB' : 'widget[open] set');
      }
    }).observe(widget, { attributes: true, attributeFilter: ['open'] });

    // If already open on init
    if (widget.hasAttribute('open') && !redirecting) {
      widget.removeAttribute('open');
      go(wasRecentFab() ? PERSONAL_AREA_URL : DUE_LIST_URL, 'widget[open] on init');
      return;
    }

    // Shadow root: watch .joy-widget-container gaining .joy-open
    var attach = function () {
      if (!widget.shadowRoot) return;
      var check = function () {
        var container = widget.shadowRoot.querySelector('.joy-widget-container.joy-open');
        if (container && !redirecting) {
          container.classList.remove('joy-open');
          container.style.display = 'none';
          go(wasRecentFab() ? PERSONAL_AREA_URL : DUE_LIST_URL, wasRecentFab() ? '.joy-open added — recent FAB' : '.joy-open class added');
        }
      };
      check();
      new MutationObserver(check).observe(widget.shadowRoot, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
        childList: true
      });
    };
    if (widget.shadowRoot) attach();
    else {
      var t = 0;
      var poll = setInterval(function () {
        if (widget.shadowRoot) { clearInterval(poll); attach(); }
        else if (++t > 100) clearInterval(poll);
      }, 50);
    }
  }

  // ---- 4) Attach handlers DIRECTLY on every joy-fab-button we can find,
  //          including inside the widget's shadow root — window/document
  //          capture is not enough because Joy may stop propagation early.
  function hardenFab(fab) {
    if (!fab || fab.__dueFabBound) return;
    fab.__dueFabBound = true;
    var handler = function (e) {
      markFab();
      go(PERSONAL_AREA_URL, 'FAB direct handler', e);
    };
    ['pointerdown', 'mousedown', 'click', 'touchstart'].forEach(function (ev) {
      fab.addEventListener(ev, handler, { capture: true, passive: false });
    });

    // Also patch inner button inside the FAB's shadow root, when available
    var bindInner = function () {
      if (!fab.shadowRoot) return;
      var inner = fab.shadowRoot.querySelector('button, .fab');
      if (inner && !inner.__dueBound) {
        inner.__dueBound = true;
        ['pointerdown', 'mousedown', 'click', 'touchstart'].forEach(function (ev) {
          inner.addEventListener(ev, handler, { capture: true, passive: false });
        });
      }
    };
    if (fab.shadowRoot) bindInner();
    else {
      var t = 0;
      var poll = setInterval(function () {
        if (fab.shadowRoot) { clearInterval(poll); bindInner(); }
        else if (++t > 60) clearInterval(poll);
      }, 50);
    }
  }

  function findAllFabs() {
    var fabs = [];
    // Light DOM
    document.querySelectorAll('joy-fab-button').forEach(function (f) { fabs.push(f); });
    // Inside every widget's shadow root
    document.querySelectorAll('joy-loyalty-widget').forEach(function (w) {
      if (w.shadowRoot) {
        w.shadowRoot.querySelectorAll('joy-fab-button').forEach(function (f) { fabs.push(f); });
      }
    });
    return fabs;
  }

  function scanFabs() {
    findAllFabs().forEach(hardenFab);
  }

  // ---- 5) LABEL: force the launcher text to FAB_LABEL ----------------------
  //
  // Joy's launcher lives in a lazily-loaded chunk and its tag/class names have
  // changed before (joy-fab-button / .joy-fab-pure are no longer present in the
  // current bundle at all), so nothing here keys on a specific element name.
  // Instead: find launcher-ish elements, and replace only text that is exactly
  // one of WRONG_LABELS.

  function normalise(s) {
    return String(s == null ? '' : s).replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function isFabLabel(s) {
    var n = normalise(s);
    if (!n) return false;
    if (n === normalise(FAB_LABEL)) return true;
    return WRONG_LABELS.indexOf(n) !== -1;
  }

  function isWrongLabel(s) {
    return WRONG_LABELS.indexOf(normalise(s)) !== -1;
  }

  // Every root Joy might render into: the document plus the shadow root of any
  // custom element it owns.
  function joyRoots() {
    var roots = [document];
    try {
      document.querySelectorAll('*').forEach(function (el) {
        var tag = (el.tagName || '').toLowerCase();
        if (tag.indexOf('joy-') === 0 && el.shadowRoot) {
          roots.push(el.shadowRoot);
          el.shadowRoot.querySelectorAll('*').forEach(function (inner) {
            if (inner.shadowRoot) roots.push(inner.shadowRoot);
          });
        }
      });
    } catch (_) {}
    return roots;
  }

  var LAUNCHER_SEL = [
    'joy-fab-button',
    '[class*="fab" i]',
    '[class*="launcher" i]',
    '[id*="fab" i]',
    '[id*="launcher" i]'
  ].join(',');

  function relabel(el) {
    var changed = false;

    // Attributes first — these are what the routing and screen readers use.
    ['aria-label', 'title'].forEach(function (attr) {
      try {
        if (isWrongLabel(el.getAttribute(attr))) {
          el.setAttribute(attr, FAB_LABEL);
          changed = true;
        }
      } catch (_) {}
    });

    // Then visible text. Only whole text nodes that are exactly a wrong label
    // are touched, so surrounding markup and point-balance bubbles survive.
    try {
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      var node;
      while ((node = walker.nextNode())) {
        if (isWrongLabel(node.nodeValue)) {
          node.nodeValue = FAB_LABEL;
          changed = true;
        }
      }
    } catch (_) {}

    return changed;
  }

  function enforceFabLabel() {
    joyRoots().forEach(function (root) {
      var nodes;
      try { nodes = root.querySelectorAll(LAUNCHER_SEL); } catch (_) { return; }
      nodes.forEach(function (el) {
        // Skip anything inside the opened widget panel — the launcher is what
        // we are after, not the "Rewards" tab in the drawer.
        try {
          if (el.closest && el.closest('.joy-widget-container')) return;
        } catch (_) {}
        relabel(el);
      });
    });
  }

  function scan() {
    var widgets = document.querySelectorAll('joy-loyalty-widget');
    for (var i = 0; i < widgets.length; i++) watchWidget(widgets[i]);
    scanFabs();
    enforceFabLabel();
  }

  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });
  scan();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  }

  // Shadow-DOM mutations do not reach the observer above, and Joy renders the
  // launcher from a lazily-loaded chunk well after this file runs. Poll for a
  // bounded window (~30s) so the label is corrected whenever the launcher
  // finally appears, then stop — this must not become a permanent timer.
  var labelTries = 0;
  var labelPoll = setInterval(function () {
    labelTries++;
    enforceFabLabel();
    if (labelTries > 60) clearInterval(labelPoll);
  }, 500);
})();
